import { useEffect, useMemo, useState } from "react"
import { Box, Card, CircularProgress, Collapse, IconButton, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tabs, Typography } from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import { CommonHyperlink, IndicatorBoxContainer } from "components/common-styles"
import SearchRequiredWarningImage from "assets/estore-FirstPage.png"
import NoRecordImage from "assets/estore-NoRecord.png"
import { WarningMessage, WarningMessageContainer, WarningMessageWrapper } from "components/common/styles"
import CustomExportButton from "components/common/custom-export-button"
import { inventoryBusiness } from "businesses/inventory-business"
import { formatDisplayDate } from "utils/date-helper"

// Columns are declared once so the export button can reuse them.
const columns = [
  { field: "ITEM", headerName: "Product" },
  { field: "DESCRIPTION", headerName: "Description" },
  { field: "WAREHOUSE", headerName: "Warehouse" },
  { field: "HELD_QUANTITY", headerName: "On Hold Qty", numeric: true },
  { field: "SUSPENSE_QUANTITY", headerName: "In Suspense Qty", numeric: true },
  { field: "IN_PROCESS_QUANTITY", headerName: "In Process Qty", numeric: true },
  { field: "DEMAND_QUANTITY", headerName: "Sales Demand Qty", numeric: true },
  { field: "AVAILABLE_TO_SELL", headerName: "Available to Sell", numeric: true },
  { field: "TOTAL_ON_HAND_QUANTITY", headerName: "Total On Hand Qty", numeric: true },
  { field: "DAYS_REMAINING", headerName: "Days Remaining", numeric: true },
]

const formatQty = v => (v == null ? "—" : Number(v).toLocaleString())

const daysRemainingColor = days => {
  if (days < 14) return { bg: "#FDECEA", fg: "#A12A1F" }
  if (days < 30) return { bg: "#FFF3E0", fg: "#A26100" }
  return { bg: "#E6F4EA", fg: "#1E6B33" }
}

// Days cover is blank when the SKU had no sales in the 28 day window (nothing
// to divide by): with stock that means more than 28 days, with none it's 0.
const daysRemainingDisplay = row => {
  const days = row.DAYS_REMAINING
  if (days == null) return Number(row.TOTAL_ON_HAND_QUANTITY) > 0 ? { label: ">28d", ...daysRemainingColor(Infinity) } : { label: "0d", ...daysRemainingColor(0) }
  return { label: `${Number(days)}d`, ...daysRemainingColor(Number(days)) }
}

// Blank values always sort last, whichever direction is chosen.
// Blank Days Remaining sorts as shown: >28d highest, 0d lowest.
const sortValue = (row, field) => {
  if (field === "DAYS_REMAINING" && row[field] == null) return Number(row.TOTAL_ON_HAND_QUANTITY) > 0 ? Infinity : 0
  return row[field]
}

const compareRows = (a, b, field, numeric, dir) => {
  const av = sortValue(a, field)
  const bv = sortValue(b, field)
  if (av === bv) return 0
  const aBlank = av == null || av === ""
  const bBlank = bv == null || bv === ""
  if (aBlank || bBlank) return aBlank === bBlank ? 0 : aBlank ? 1 : -1
  const diff = numeric ? Number(av) - Number(bv) : String(av).localeCompare(String(bv), undefined, { numeric: true })
  return dir === "asc" ? diff : -diff
}

const getRowKey = row => `${row.ITEM}|${row.WAREHOUSE || ""}`

const filterLotsByWarehouse = (lots, selectedWarehouse) => {
  if (!Array.isArray(lots) || selectedWarehouse === ALL_TAB) return lots || []
  return lots.filter(lot => (lot?.warehouse || "") === selectedWarehouse)
}

const InventoryRow = ({ row, detailLots, isDetailLoading, detailError, onExpandRow }) => {
  const [open, setOpen] = useState(false)
  const lots = detailLots || []
  const hasLots = lots.length > 0
  const chip = daysRemainingDisplay(row)

  const handleToggle = () => {
    const nextOpen = !open
    setOpen(nextOpen)
    if (nextOpen) {
      onExpandRow(row)
    }
  }

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: open ? "unset" : undefined } }} hover>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={handleToggle} aria-label="expand row">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <CommonHyperlink to={`/products/${encodeURIComponent(row.ITEM)}`} state={{ from: "/inventory" }}>
            {row.ITEM}
          </CommonHyperlink>
        </TableCell>
        <TableCell>{row.DESCRIPTION}</TableCell>
        <TableCell>{row.WAREHOUSE}</TableCell>
        <TableCell align="right">{formatQty(row.HELD_QUANTITY)}</TableCell>
        <TableCell align="right">{formatQty(row.SUSPENSE_QUANTITY)}</TableCell>
        <TableCell align="right">{formatQty(row.IN_PROCESS_QUANTITY)}</TableCell>
        <TableCell align="right">{formatQty(row.DEMAND_QUANTITY)}</TableCell>
        <TableCell align="right">{formatQty(row.AVAILABLE_TO_SELL)}</TableCell>
        <TableCell align="right">{formatQty(row.TOTAL_ON_HAND_QUANTITY)}</TableCell>
        <TableCell align="right">
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontWeight: 700,
              fontSize: 12,
              bgcolor: chip.bg,
              color: chip.fg,
              minWidth: 44,
              textAlign: "center",
            }}
          >
            {chip.label}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ p: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 4, py: 2, bgcolor: "#FAFAFA" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Lots & batches
              </Typography>
              {isDetailLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading inventory details...
                  </Typography>
                </Stack>
              ) : detailError ? (
                <Typography variant="body2" color="error.main">
                  Failed to load inventory details.
                </Typography>
              ) : hasLots ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Warehouse</TableCell>
                      <TableCell>Lot</TableCell>
                      <TableCell>Expiry</TableCell>
                      <TableCell align="right">Available to Sell</TableCell>
                      <TableCell align="right">On Hand</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lots.map((lot, i) => (
                      <TableRow key={`${lot.lot || "(no-lot)"}-${i}`}>
                        <TableCell>{lot.warehouse || "—"}</TableCell>
                        <TableCell>{lot.lot || "—"}</TableCell>
                        <TableCell>{lot.expiration_date ? formatDisplayDate(lot.expiration_date) : "—"}</TableCell>
                        <TableCell align="right">{formatQty(lot.available_to_sell)}</TableCell>
                        <TableCell align="right">{formatQty(lot.on_hand_quantity)}</TableCell>
                        <TableCell>{lot.inventory_status || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No lot breakdown returned for this row.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]
const ALL_TAB = "ALL"

const InventoryTable = ({ data, isLoading, searchText }) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [selectedWarehouse, setSelectedWarehouse] = useState(ALL_TAB)
  const [sort, setSort] = useState({ field: null, dir: "asc" })
  const [detailsByRow, setDetailsByRow] = useState({})
  const [loadingDetailsByRow, setLoadingDetailsByRow] = useState({})
  const [detailErrorByRow, setDetailErrorByRow] = useState({})

  const warehouses = useMemo(() => {
    const set = new Set((data || []).map(r => r.WAREHOUSE).filter(Boolean))
    return Array.from(set).sort()
  }, [data])

  useEffect(() => {
    if (selectedWarehouse !== ALL_TAB && !warehouses.includes(selectedWarehouse)) {
      setSelectedWarehouse(ALL_TAB)
    }
  }, [warehouses, selectedWarehouse])

  const filteredRows = useMemo(() => {
    if (selectedWarehouse === ALL_TAB) return data
    return data.filter(r => r.WAREHOUSE === selectedWarehouse)
  }, [data, selectedWarehouse])

  // Reset to page 0 whenever the aggregate dataset changes (e.g. customer
  // switch, new filters) so we don't end up on an out-of-range page.
  useEffect(() => {
    setPage(0)
  }, [data.length])

  useEffect(() => {
    setDetailsByRow({})
    setLoadingDetailsByRow({})
    setDetailErrorByRow({})
  }, [data])

  const loadRowDetails = async row => {
    const rowKey = getRowKey(row)
    if (detailsByRow[rowKey] || loadingDetailsByRow[rowKey]) return

    setLoadingDetailsByRow(prev => ({ ...prev, [rowKey]: true }))
    setDetailErrorByRow(prev => ({ ...prev, [rowKey]: false }))

    try {
      const response = await inventoryBusiness.getProductDetails({
        product_code: row.ITEM,
        id: 0,
        archival_search: false,
      })

      // Keep the full lot list in cache so changing tabs can re-filter
      // details immediately without showing stale results.
      const lots = response?.inventory || []
      setDetailsByRow(prev => ({ ...prev, [rowKey]: lots }))
    } catch (error) {
      setDetailErrorByRow(prev => ({ ...prev, [rowKey]: true }))
      setDetailsByRow(prev => ({ ...prev, [rowKey]: [] }))
    } finally {
      setLoadingDetailsByRow(prev => ({ ...prev, [rowKey]: false }))
    }
  }

  const sortedRows = useMemo(() => {
    if (!sort.field) return filteredRows
    const numeric = columns.find(c => c.field === sort.field)?.numeric
    return [...filteredRows].sort((a, b) => compareRows(a, b, sort.field, numeric, sort.dir))
  }, [filteredRows, sort])

  const handleSort = field => {
    setSort(prev => ({ field, dir: prev.field === field && prev.dir === "asc" ? "desc" : "asc" }))
    setPage(0)
  }

  const paged = useMemo(() => sortedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage), [sortedRows, page, rowsPerPage])

  if (isLoading) {
    return (
      <WarningMessageContainer>
        <IndicatorBoxContainer>
          <CircularProgress size={64} />
        </IndicatorBoxContainer>
      </WarningMessageContainer>
    )
  }

  if (!searchText || data.length === 0) {
    return (
      <WarningMessageContainer>
        <WarningMessageWrapper>
          <Box component="img" sx={{ maxWidth: 200 }} src={!searchText ? SearchRequiredWarningImage : NoRecordImage} />
          <WarningMessage>{!searchText ? "Search to load inventory." : "No inventory found for the current filters."}</WarningMessage>
        </WarningMessageWrapper>
      </WarningMessageContainer>
    )
  }

  return (
    <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ p: 1, borderBottom: "1px solid #EEE" }}>
        <Tabs value={selectedWarehouse} onChange={(_, val) => setSelectedWarehouse(val)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40 }}>
          <Tab label="All" value={ALL_TAB} sx={{ minHeight: 40 }} />
          {warehouses.map(w => (
            <Tab key={w} label={w} value={w} sx={{ minHeight: 40 }} />
          ))}
        </Tabs>
        <CustomExportButton
          exportOptions={{
            columns,
            data: data,
            fileName: "Express_Inventory_Levels",
            hiddenColumns: {},
          }}
        />
      </Stack>
      <TableContainer sx={{ flex: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              {columns.map(c => (
                <TableCell key={c.field} align={c.numeric ? "right" : "left"} sx={{ fontWeight: 700 }} sortDirection={sort.field === c.field ? sort.dir : false}>
                  <TableSortLabel active={sort.field === c.field} direction={sort.field === c.field ? sort.dir : "asc"} onClick={() => handleSort(c.field)}>
                    {c.headerName}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map(row => (
              <InventoryRow
                key={row.id ?? getRowKey(row)}
                row={row}
                detailLots={filterLotsByWarehouse(detailsByRow[getRowKey(row)], selectedWarehouse)}
                isDetailLoading={Boolean(loadingDetailsByRow[getRowKey(row)])}
                detailError={Boolean(detailErrorByRow[getRowKey(row)])}
                onExpandRow={loadRowDetails}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={e => {
          setRowsPerPage(parseInt(e.target.value, 10))
          setPage(0)
        }}
      />
    </Card>
  )
}

export default InventoryTable
